import { useTranslation } from 'react-i18next';
import {
  IpponButton,
  IpponHSpace,
  IpponHSpaceSlot,
  IpponIconTile,
  IpponText,
  IpponTitle,
  IpponVSpace,
} from '@ippon-ui/react';
import type { Optional } from '@/generic/functional';
import { useDialog } from '@/generic/react-tools';
import { LoadingErrorComponent } from '@/generic/loader';
import type { Page } from '@/generic/pagination';
import type { DraftRule } from '@/rules-management/domain/DraftRule.ts';
import {
  type DraftRulesActionsProps,
  DraftRulesPublicationComponent,
} from '@/rules-management/primary/DraftRulesPublicationComponent.tsx';

const DraftRulesLoadingComponent = () => (
  <div className="ippon-modal--body" aria-busy="true">
    <IpponText variant="body" placeholder />
  </div>
);

export const DraftRulesComponent = ({
  open,
  drafts,
  failure,
  selection,
  publicationFailure,
  discardFailure,
  editFailure,
  onSelectionChange,
  onPageChange,
  onPublish,
  onDiscard,
  onSave,
  onClose,
}: DraftRulesActionsProps & {
  open: boolean;
  drafts: Optional<Page<DraftRule>>;
  failure: Optional<Error>;
}) => {
  const { t } = useTranslation('rules-management');
  const dialog = useDialog(open, onClose);

  return (
    <dialog className="ippon-modal" data-selector="rules-management.rules.drafts" ref={dialog}>
      <div className="ippon-modal--header">
        <IpponHSpace align="middle" gap={16}>
          <IpponIconTile icon={{ name: 'file-tray-full', variant: 'outline' }} size="small" />
          <IpponHSpaceSlot expand={true}>
            <IpponVSpace>
              <hgroup>
                <IpponTitle tag="h2" level={5} dataSelector="rules-management.rules.drafts.title">
                  {t('rules-management.rules.drafts.title')}
                </IpponTitle>
                <IpponText
                  variant="body"
                  color="neutral-tertiary-inversed"
                  tag="p"
                  dataSelector="rules-management.rules.drafts.subtitle"
                >
                  {t('rules-management.rules.drafts.subtitle')}
                </IpponText>
              </hgroup>
            </IpponVSpace>
          </IpponHSpaceSlot>
          <IpponButton
            color="neutral"
            variant="text"
            onClick={onClose}
            iconLeft={{ name: 'close' }}
            dataSelector="rules-management.rules.drafts.close"
          />
        </IpponHSpace>
      </div>
      <hr className="ippon-modal--separator" />
      {failure
        .map((error) => (
          <div className="ippon-modal--body" key="rules-management.rules.drafts.error">
            <LoadingErrorComponent
              error={error}
              dataSelector="rules-management.rules.drafts.error"
            />
          </div>
        ))
        .orElseGet(() =>
          drafts
            .map((page) => (
              <DraftRulesPublicationComponent
                key="rules-management.rules.drafts.content"
                drafts={page}
                selection={selection}
                publicationFailure={publicationFailure}
                discardFailure={discardFailure}
                editFailure={editFailure}
                onSelectionChange={onSelectionChange}
                onPageChange={onPageChange}
                onPublish={onPublish}
                onDiscard={onDiscard}
                onSave={onSave}
                onClose={onClose}
              />
            ))
            .orElseGet(() => (
              <DraftRulesLoadingComponent key="rules-management.rules.drafts.loading" />
            )),
        )}
    </dialog>
  );
};
